import { ExternalLink, BookOpen, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { useUIStore } from '../../stores/uiStore'

interface SourcesListProps {
  sources: string[]
}

export default function SourcesList({ sources }: SourcesListProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const showToast = useUIStore((state) => state.showToast)

  // Format URL for display - show full URL or truncated version
  const formatSource = (url: string) => {
    try {
      const urlObj = new URL(url)
      const path = urlObj.pathname + urlObj.search

      // If path is long, truncate it
      if (path.length > 30) {
        return `${urlObj.hostname}${path.substring(0, 27)}...`
      }

      return `${urlObj.hostname}${path}`
    } catch {
      return url
    }
  }

  const handleCopy = async (url: string, index: number) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedIndex(index)
      showToast('URL copied to clipboard', 'success')

      setTimeout(() => {
        setCopiedIndex(null)
      }, 2000)
    } catch (error) {
      showToast('Failed to copy URL', 'error')
    }
  }

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-[var(--bbs-text-tertiary)]" />
        <h4 className="font-medium text-[var(--bbs-text-secondary)]">
          Sources
        </h4>
      </div>

      <ul className="space-y-2">
        {sources.map((source, index) => (
          <li key={index} className="group">
            <div className="flex items-center gap-2">
              <a
                href={source}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-2 rounded-lg flex-1
                  text-sm text-[var(--bbs-text-accent)]
                  hover:bg-[var(--luxury-glass-neutral)]
                  transition-colors"
                title={source}
              >
                <ExternalLink className="w-4 h-4 flex-shrink-0" />
                <span className="truncate font-mono text-xs">
                  {formatSource(source)}
                </span>
              </a>

              <button
                type="button"
                onClick={() => handleCopy(source, index)}
                className="p-2 rounded-lg
                  text-[var(--bbs-text-tertiary)]
                  hover:text-[var(--bbs-text-accent)]
                  hover:bg-[var(--luxury-glass-neutral)]
                  transition-colors
                  opacity-0 group-hover:opacity-100"
                title="Copy URL"
              >
                {copiedIndex === index ? (
                  <Check className="w-4 h-4 text-[var(--bbs-color-success)]" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
